<!--
 * Path: packages/frontend/src/features/browser/forms/relations/Relations.vue
 * Task: Wrapper for single-select relations utilizing the FormBlockWrapper.
 * Refactoring: Linked saving mechanics to the new UI and Workspace stores and decoupled permissions.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :disable-edit="field.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('show-info')"
  >
    <template #read>
      <RelationsRead :field="field" :hide-header="true" @show-info="$emit('show-info')" />
    </template>

    <template #edit>
      <RelationsEdit :field="field" @show-info="$emit('show-info')" />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import RelationsRead from './RelationsRead.vue';
import RelationsEdit from './RelationsEdit.vue';

interface FieldProps {
  id: string;
  name: string;
  info?: string;
  entitySource?: string;
  readonly?: boolean;
}

const props = defineProps<{ field: FieldProps }>();

const emit = defineEmits<{ (e: 'show-info', payload: { title: string; text?: string }): void; }>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.field.readonly);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};
</script>
